<template>
  <v-card
    v-if="variant === 'landscape'"
    class="partner-card-preview partner-card-preview--landscape d-flex flex-column"
    :class="{ 'partner-card-preview--fluid': fluid }"
    outlined
  >
    <template v-if="logoLayout === 'wide'">
      <div class="d-flex flex-row flex-grow-1">
        <div class="partner-wide-landscape-left pa-3 d-flex flex-column">
          <PartnerLogoBlock
            :src="logoSrc"
            :alt="card.company_name"
            container-class="partner-logo--wide-landscape-logo mb-3"
            :fallback-icon-size="48"
          />
          <div class="partner-header-text mb-2">
            <div class="text-subtitle-1 font-weight-bold primary-grey--text">
              {{ card.company_name || 'Partner Name' }}
            </div>
            <div class="text-caption grey--text">
              {{ card.descriptor || 'Descriptor text' }}
            </div>
          </div>
          <div class="mt-auto d-flex">
            <v-btn
              v-for="(cta, index) in displayCtas"
              :key="index"
              :outlined="isOutlinedButton(index)"
              color="primary"
              :class="{
                'white--text': !isOutlinedButton(index),
                'placeholder-btn': !cta.label,
              }"
              small
              class="partner-cta-btn mr-2 flex-grow-1"
              @click="handleCtaClick(cta)"
            >
              <v-icon
                v-if="ctaIcon(cta.type)"
                small
                left
              >
                {{ ctaIcon(cta.type) }}
              </v-icon>
              {{ cta.label || 'Button Text' }}
            </v-btn>
          </div>
        </div>
        <div class="partner-landscape-divider" />
        <div class="partner-landscape-right pa-4 d-flex flex-column flex-grow-1 justify-center position-relative">
          <PartnerCardBadge
            class="partner-badge--top-right"
            badge-class="partner-badge--small"
            label="A trusted GrainFox Partner"
            :card-id="card.id"
            :interactive="interactive"
          />
          <div
            class="text-body-2 font-weight-bold primary-grey--text mb-1 partner-headline"
            :class="{ 'grey--text': !card.headline }"
          >
            {{ card.headline || 'Headline will appear here' }}
          </div>
          <div
            class="text-body-2 primary-grey--text partner-body"
            :class="{ 'grey--text': !card.body }"
          >
            {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="logoLayout === 'tall' || logoLayout === 'square'">
      <div class="d-flex flex-row flex-grow-1 pa-3">
        <PartnerLogoBlock
          :src="logoSrc"
          :alt="card.company_name"
          :container-class="logoLayout === 'tall' ? 'partner-logo--tall-strip mr-3' : 'partner-logo--square mr-3'"
          :fallback-icon-size="40"
        />
        <div class="partner-landscape-divider" />
        <div class="pa-4 d-flex flex-column flex-grow-1 justify-center position-relative">
          <PartnerCardBadge
            class="partner-badge--top-right"
            badge-class="partner-badge--small"
            label="A trusted GrainFox Partner"
            :card-id="card.id"
            :interactive="interactive"
          />
          <div class="partner-header-text mb-2">
            <div class="text-subtitle-1 font-weight-bold primary-grey--text">
              {{ card.company_name || 'Partner Name' }}
            </div>
            <div class="text-caption grey--text">
              {{ card.descriptor || 'Descriptor text' }}
            </div>
          </div>
          <div
            class="text-body-2 font-weight-bold primary-grey--text mb-1 partner-headline"
            :class="{ 'grey--text': !card.headline }"
          >
            {{ card.headline || 'Headline will appear here' }}
          </div>
          <div
            class="text-body-2 primary-grey--text partner-body mb-3"
            :class="{ 'grey--text': !card.body }"
          >
            {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
          </div>
          <div class="d-flex">
            <v-btn
              v-for="(cta, index) in displayCtas"
              :key="index"
              :outlined="isOutlinedButton(index)"
              color="primary"
              :class="{
                'white--text': !isOutlinedButton(index),
                'placeholder-btn': !cta.label,
              }"
              small
              class="partner-cta-btn mr-2"
              @click="handleCtaClick(cta)"
            >
              <v-icon
                v-if="ctaIcon(cta.type)"
                small
                left
              >
                {{ ctaIcon(cta.type) }}
              </v-icon>
              {{ cta.label || 'Button Text' }}
            </v-btn>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="d-flex flex-row flex-grow-1 pa-3">
        <PartnerLogoBlock
          :src="logoSrc"
          :alt="card.company_name"
          container-class="partner-logo--rounded mr-3"
          :fallback-icon-size="40"
        />
        <div class="partner-landscape-divider" />
        <div class="pa-4 d-flex flex-column flex-grow-1 justify-center position-relative">
          <PartnerCardBadge
            class="partner-badge--top-right"
            badge-class="partner-badge--small"
            :card-id="card.id"
            :interactive="interactive"
          />
          <div class="partner-header-text mb-2">
            <div class="text-subtitle-1 font-weight-bold primary-grey--text">
              {{ card.company_name || 'Partner Name' }}
            </div>
            <div class="text-caption grey--text">
              {{ card.descriptor || 'Descriptor text' }}
            </div>
          </div>
          <div
            class="text-body-2 font-weight-bold primary-grey--text mb-1 partner-headline"
            :class="{ 'grey--text': !card.headline }"
          >
            {{ card.headline || 'Headline will appear here' }}
          </div>
          <div
            class="text-body-2 primary-grey--text partner-body mb-3"
            :class="{ 'grey--text': !card.body }"
          >
            {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
          </div>
          <div class="d-flex">
            <v-btn
              v-for="(cta, index) in displayCtas"
              :key="index"
              :outlined="isOutlinedButton(index)"
              color="primary"
              :class="{
                'white--text': !isOutlinedButton(index),
                'placeholder-btn': !cta.label,
              }"
              small
              class="partner-cta-btn mr-2"
              @click="handleCtaClick(cta)"
            >
              <v-icon
                v-if="ctaIcon(cta.type)"
                small
                left
              >
                {{ ctaIcon(cta.type) }}
              </v-icon>
              {{ cta.label || 'Button Text' }}
            </v-btn>
          </div>
        </div>
      </div>
    </template>
  </v-card>

  <div
    v-else-if="variant === 'side'"
    class="partner-side-panel d-flex flex-column"
  >
    <template v-if="logoLayout === 'wide'">
      <div class="px-4 py-4">
        <PartnerCardBadge
          class="grey--text mb-2"
          label="This report brought to you by a trusted GrainFox Partner"
          :card-id="card.id"
          :interactive="interactive"
        />
        <PartnerLogoBlock
          :src="logoSrc"
          :alt="card.company_name"
          container-class="partner-logo--wide-banner-side mt-2"
          :fallback-icon-size="48"
        />
      </div>
      <div class="pa-4 pt-3 d-flex flex-column flex-grow-1">
        <div class="partner-header-text mb-3">
          <div class="text-subtitle-1 font-weight-bold primary-grey--text">
            {{ card.company_name || 'Partner Name' }}
          </div>
          <div class="text-caption grey--text">
            {{ card.descriptor || 'Descriptor text' }}
          </div>
        </div>
        <div
          class="text-body-2 font-weight-bold primary-grey--text mb-2 partner-headline"
          :class="{ 'grey--text': !card.headline }"
        >
          {{ card.headline || 'Headline will appear here' }}
        </div>
        <div
          class="text-body-2 primary-grey--text partner-body"
          :class="{ 'grey--text': !card.body }"
        >
          {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
        </div>
      </div>
      <div class="px-4 pb-4 pt-0">
        <v-btn
          v-for="(cta, index) in displayCtas"
          :key="index"
          :outlined="isOutlinedButton(index)"
          color="primary"
          :class="{
            'white--text': !isOutlinedButton(index),
            'placeholder-btn': !cta.label,
          }"
          small
          block
          class="partner-cta-btn mb-2"
          @click="handleCtaClick(cta)"
        >
          <v-icon
            v-if="ctaIcon(cta.type)"
            small
            left
          >
            {{ ctaIcon(cta.type) }}
          </v-icon>
          {{ cta.label || 'Button Text' }}
        </v-btn>
      </div>
    </template>

    <template v-else-if="logoLayout === 'square'">
      <div class="px-4 py-4">
        <PartnerCardBadge
          class="grey--text mb-2"
          label="This report brought to you by a trusted GrainFox Partner"
          :card-id="card.id"
          :interactive="interactive"
        />
      </div>
      <div class="pa-4 pt-3 d-flex flex-column flex-grow-1">
        <div class="d-flex align-center mb-3">
          <PartnerLogoBlock
            :src="logoSrc"
            :alt="card.company_name"
            container-class="partner-logo--square-side mr-3"
            :fallback-icon-size="36"
          />
          <div class="partner-header-text">
            <div class="text-subtitle-1 font-weight-bold primary-grey--text">
              {{ card.company_name || 'Partner Name' }}
            </div>
            <div class="text-caption grey--text">
              {{ card.descriptor || 'Descriptor text' }}
            </div>
          </div>
        </div>
        <div
          class="text-body-2 font-weight-bold primary-grey--text mb-2 partner-headline"
          :class="{ 'grey--text': !card.headline }"
        >
          {{ card.headline || 'Headline will appear here' }}
        </div>
        <div
          class="text-body-2 primary-grey--text partner-body"
          :class="{ 'grey--text': !card.body }"
        >
          {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
        </div>
      </div>
      <div class="px-4 pb-4 pt-0">
        <v-btn
          v-for="(cta, index) in displayCtas"
          :key="index"
          :outlined="isOutlinedButton(index)"
          color="primary"
          :class="{
            'white--text': !isOutlinedButton(index),
            'placeholder-btn': !cta.label,
          }"
          small
          block
          class="partner-cta-btn mb-2"
          @click="handleCtaClick(cta)"
        >
          <v-icon
            v-if="ctaIcon(cta.type)"
            small
            left
          >
            {{ ctaIcon(cta.type) }}
          </v-icon>
          {{ cta.label || 'Button Text' }}
        </v-btn>
      </div>
    </template>

    <template v-else-if="logoLayout === 'tall'">
      <div class="d-flex flex-grow-1 pa-4 pb-0">
        <PartnerLogoBlock
          :src="logoSrc"
          :alt="card.company_name"
          container-class="partner-logo--tall-strip mr-3"
          :fallback-icon-size="40"
        />
        <div class="d-flex flex-column flex-grow-1">
          <span class="partner-badge grey--text mb-2">
            This report brought to you by a trusted <br> GrainFox Partner
          </span>
          <div class="partner-header-text mb-2">
            <div class="text-subtitle-1 font-weight-bold primary-grey--text">
              {{ card.company_name || 'Partner Name' }}
            </div>
            <div class="text-caption grey--text">
              {{ card.descriptor || 'Descriptor text' }}
            </div>
          </div>
          <div
            class="text-body-2 font-weight-bold primary-grey--text mb-2 partner-headline"
            :class="{ 'grey--text': !card.headline }"
          >
            {{ card.headline || 'Headline will appear here' }}
          </div>
          <div
            class="text-body-2 primary-grey--text partner-body"
            :class="{ 'grey--text': !card.body }"
          >
            {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
          </div>
        </div>
      </div>
      <div class="px-4 pb-4 pt-3">
        <v-btn
          v-for="(cta, index) in displayCtas"
          :key="index"
          :outlined="isOutlinedButton(index)"
          color="primary"
          :class="{
            'white--text': !isOutlinedButton(index),
            'placeholder-btn': !cta.label,
          }"
          small
          block
          class="partner-cta-btn mb-2"
          @click="handleCtaClick(cta)"
        >
          <v-icon
            v-if="ctaIcon(cta.type)"
            small
            left
          >
            {{ ctaIcon(cta.type) }}
          </v-icon>
          {{ cta.label || 'Button Text' }}
        </v-btn>
      </div>
    </template>

    <template v-else>
      <div class="px-4 py-4">
        <PartnerCardBadge
          class="grey--text"
          label="This report brought to you by a trusted GrainFox Partner"
          :card-id="card.id"
          :interactive="interactive"
        />
      </div>
      <div class="pa-4 pt-3 d-flex flex-column flex-grow-1">
        <div class="d-flex align-center mb-3">
          <PartnerLogoBlock
            :src="logoSrc"
            :alt="card.company_name"
            container-class="partner-logo--rounded-side mr-3"
            :fallback-icon-size="36"
          />
          <div class="partner-header-text">
            <div class="text-subtitle-1 font-weight-bold primary-grey--text">
              {{ card.company_name || 'Partner Name' }}
            </div>
            <div class="text-caption grey--text">
              {{ card.descriptor || 'Descriptor text' }}
            </div>
          </div>
        </div>
        <div
          class="text-body-2 font-weight-bold primary-grey--text mb-2 partner-headline"
          :class="{ 'grey--text': !card.headline }"
        >
          {{ card.headline || 'Headline will appear here' }}
        </div>
        <div
          class="text-body-2 primary-grey--text partner-body"
          :class="{ 'grey--text': !card.body }"
        >
          {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
        </div>
      </div>
      <div class="px-4 pb-4 pt-0">
        <v-btn
          v-for="(cta, index) in displayCtas"
          :key="index"
          :outlined="isOutlinedButton(index)"
          color="primary"
          :class="{
            'white--text': !isOutlinedButton(index),
            'placeholder-btn': !cta.label,
          }"
          small
          block
          class="partner-cta-btn mb-2"
          @click="handleCtaClick(cta)"
        >
          <v-icon
            v-if="ctaIcon(cta.type)"
            small
            left
          >
            {{ ctaIcon(cta.type) }}
          </v-icon>
          {{ cta.label || 'Button Text' }}
        </v-btn>
      </div>
    </template>
  </div>

  <v-card
    v-else
    class="partner-card-preview d-flex flex-column"
    :class="{ 'partner-card-preview--fluid': fluid }"
    outlined
  >
    <v-card-text class="pa-3 pb-2 d-flex flex-column flex-grow-1">
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-caption font-italic grey--text">A trusted GrainFox Partner</span>
        <div class="d-flex align-center">
          <v-btn
            v-if="closable"
            icon
            x-small
            class="text-caption grey--text text-none"
            @click="$emit('close')"
          >
            Close
            <v-icon
              class="mr-6"
              small
            >
              mdi-chevron-right
            </v-icon>
          </v-btn>
          <PartnerFeedbackMenu
            v-if="interactive && card.id"
            :card-id="card.id"
          />
        </div>
      </div>
      <div class="d-flex align-center mb-2">
        <PartnerLogoBlock
          :src="logoSrc"
          :alt="card.company_name"
          container-class="partner-logo--compact mr-3"
          :fallback-icon-size="22"
        />
        <div style="min-width: 0; overflow: hidden;">
          <div class="text-subtitle-2 font-weight-bold primary-grey--text">
            {{ card.company_name || 'Partner Name' }}
          </div>
          <div class="text-caption grey--text">
            {{ card.descriptor || 'Descriptor text' }}
          </div>
        </div>
      </div>
      <div
        class="text-body-2 font-weight-medium primary-grey--text mb-1 partner-headline"
        :class="{ 'grey--text': !card.headline }"
      >
        {{ card.headline || 'Headline will appear here' }}
      </div>
      <div
        class="text-body-2 primary-grey--text partner-body"
        :class="{ 'grey--text': !card.body }"
      >
        {{ card.body || 'Body text will appear here. This area shows the main content of the partner card.' }}
      </div>
    </v-card-text>
    <v-card-actions class="px-3 pb-3 pt-0">
      <v-btn
        v-for="(cta, index) in displayCtas"
        :key="index"
        :outlined="isOutlinedButton(index)"
        color="primary"
        :class="{
          'white--text': !isOutlinedButton(index),
          'placeholder-btn': !cta.label,
        }"
        small
        class="partner-cta-btn"
        style="flex: 1 1 0; min-width: 0;"
        @click="handleCtaClick(cta)"
      >
        <v-icon
          v-if="ctaIcon(cta.type)"
          small
          left
        >
          {{ ctaIcon(cta.type) }}
        </v-icon>
        {{ cta.label || 'Button Text' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import PartnerLogoBlock from './PartnerLogoBlock.vue';
import PartnerCardBadge from './PartnerCardBadge.vue';
import PartnerFeedbackMenu from './PartnerFeedbackMenu.vue';

const CTA_ICONS = {
  link: 'mdi-open-in-new',
  phone: 'mdi-phone',
  email: 'mdi-email',
};

export default {
  name: 'PartnerCardPreview',

  components: {
    PartnerLogoBlock,
    PartnerCardBadge,
    PartnerFeedbackMenu,
  },

  props: {
    card: {
      type: Object,
      required: true,
    },
    logoPreview: {
      type: String,
      default: null,
    },
    interactive: {
      type: Boolean,
      default: false,
    },
    fluid: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'landscape', 'side'].includes(v),
    },
    closable: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    logoSrc() {
      return this.logoPreview || this.card.logo_url || null;
    },
    logoLayout() {
      return this.card.logo_layout || 'rounded';
    },
    activeCtas() {
      if (!this.card.ctas) return [];
      return this.card.ctas.filter((cta) => cta.is_active !== false);
    },
    displayCtas() {
      if (this.activeCtas.length > 0) return this.activeCtas;
      return [{ type: 'link', label: '', is_active: true }];
    },
  },

  methods: {
    ctaIcon(type) {
      return CTA_ICONS[type] || null;
    },
    isOutlinedButton(index) {
      if (this.displayCtas.length <= 1) return false;
      const cta = this.displayCtas[index];
      if (cta.is_primary !== undefined) return !cta.is_primary;
      return index > 0;
    },
    handleCtaClick(cta) {
      if (!this.interactive || !cta.value) return;
      this.$emit('cta-click', cta);
      if (cta.type === 'link') {
        window.open(cta.value, '_blank', 'noopener,noreferrer');
      } else if (cta.type === 'email') {
        window.location.href = `mailto:${cta.value}`;
      } else if (cta.type === 'phone') {
        window.location.href = `tel:${cta.value}`;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/colors/colors";

.partner-card-preview {
  border: 1px solid colors.$primary !important;
  background-color: colors.$greyLighten4 !important;
  border-radius: 8px !important;
  width: 320px;
  min-width: 280px;
  max-width: 360px;
}

.partner-card-preview--landscape {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.partner-card-preview--fluid {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.partner-landscape-divider {
  width: 1px;
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.12);
  margin: 12px 0;
}

.partner-landscape-right {
  min-width: 0;
}

.partner-badge--top-right {
  position: absolute;
  top: 8px;
  right: 12px;
  width: auto;
  text-align: right;
}

.partner-header-text {
  flex: 1;
  min-width: 0;
}

.partner-headline {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.partner-body {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.partner-cta-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  padding: 0 12px !important;
}

.partner-cta-btn .v-icon {
  margin-right: 6px;
}

.placeholder-btn {
  opacity: 0.5;
}

.partner-side-panel {
  background-color: transparent;
  width: 100%;
}

.partner-logo {
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: colors.$white;
}

.partner-wide-landscape-left {
  flex: 0 0 auto;
}

.partner-logo--square {
  width: 112px;
  height: 112px;
  border-radius: 8px;
  flex-shrink: 0;
  align-self: center;
}

.partner-logo--square-side {
  width: 96px;
  height: 96px;
  border-radius: 8px;
  flex-shrink: 0;
  align-self: center;
}

.partner-logo--rounded {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  flex-shrink: 0;
  align-self: center;
}

.partner-logo--rounded-side {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  flex-shrink: 0;
  align-self: center;
}

.partner-logo--wide-landscape-logo {
  max-width: 200pt;
  height: 80px;
  border-radius: 8px;
}

.partner-logo--wide-banner-side {
  width: 100%;
  height: 100px;
  border-radius: 8px;
  margin-bottom: 8px;
}

.partner-logo--compact {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  flex-shrink: 0;
}

.partner-logo--tall-strip {
  width: 80px;
  min-height: 120px;
  border-radius: 8px;
  align-self: stretch;
}
</style>
